import {Check, Flame, Heart, Logs, Timer, TimerOff, TimerReset, Trophy, X} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {useNavigate} from "react-router-dom";
import {Poll, PollStatus} from "@/types/Poll.ts";
import {formatDistanceToNow} from 'date-fns';
import {useIsMobile} from "@/hooks/use-mobile";
import * as React from "react";
import {useState} from "react";
import CountdownTimer from "./CountdownTimer";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,} from "@/components/ui/tooltip";
import {VoteConfirmationModal} from "./VoteConfirmationModal";
import {toast} from "sonner";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {useLoginModal} from "@/contexts/LoginModalContext.tsx";

interface PollListItemProps {
    poll: Poll,
    refetch?: () => void,
}

export const PollListItem = ({poll, refetch}: PollListItemProps) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const {user, session, loading} = useAuth();
    const {openLoginModal} = useLoginModal();
    const [voteStatus, setVoteStatus] = useState<'none' | 'yes' | 'no' | 'third'>('none');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingVote, setPendingVote] = useState<{
        type: 'yes' | 'no' | 'third',
        option: string,
        id: string
    } | null>(null);

    /*const [userSession] = useState<Session | null>(() => {
        const cachedSession = localStorage.getItem('session');
        return cachedSession ? JSON.parse(cachedSession) : null;
    });*/

    // console.log("poll: ",poll)
    const userVote = poll.options.find(p => p.id == poll.userVotes?.find(u => u.poll_id == poll.id)?.option_id);
    // console.log("userVote: ", userVote);
    const handlePollClick = () => {
        const slug = poll.slug;
        navigate(`/polls/${slug}`, {replace: false});
    };


    const handleSubmit = async (id: string) => {
        if (!session) {
            return toast.error('Please login to cast your vote');
        }

        try {
            const url = `${import.meta.env.VITE_API_URL}/polls/vote`;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            };
            const body = JSON.stringify({pollId: poll.id, optionId: id});

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body,
            });

            if (!response.ok) {
                return toast.error('An error occurred while saving vote!');
            }

            // await queryClient.invalidateQueries({queryKey: ['userVote', pollDetail]});
            // await queryClient.invalidateQueries({queryKey: ['polls', slug]});
            await refetch();
            setShowConfirmModal(false);
            setPendingVote(null);
            return toast.success('Your vote has been saved!');
        } catch (error) {
            // console.error('Error:', error);
        }

    }

    const handleVoteClick = (vote: 'yes' | 'no' | 'third', optionTitle: string, e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!loading && (!user || !session)) {
            // console.log("hello");
            openLoginModal();
            return;
        }
        setPendingVote({type: vote, option: optionTitle, id});
        setShowConfirmModal(true);
    };

    const handleConfirmVote = async () => {
        if (pendingVote) {
            setVoteStatus(pendingVote.type);
            await handleSubmit(pendingVote.id);

            // Here you would typically call an API to record the vote
        }
    };

    const handleCancelVote = () => {
        setShowConfirmModal(false);
        setPendingVote(null);
    };

    const formatEndDate = (endDate: Date) => {
        const now = new Date();

        if (new Date(endDate) < new Date(now)) {
            return 'Time is up';
        }

        return formatDistanceToNow(endDate, {addSuffix: true});
    };

    const formatNumber = (num: number): string => {
        if (num >= 1000000) {
            return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
        }
        if (num >= 1000) {
            return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K`;
        }
        return num.toString();
    };

    const getPollStatus = (endDate: Date, status: PollStatus) => {
        const now = new Date();
        const timeLeft = new Date(endDate).getTime() - now.getTime();
        const hoursLeft = timeLeft / (1000 * 60 * 60);

        if (status == PollStatus.active) {
            if (new Date(endDate) < new Date(now)) {
                return {color: "text-red-500", title: "Finished", icon: <TimerReset className="w-4 h-4 text-red-500"/>};
            } else if (hoursLeft <= 24) {
                return {
                    color: "text-orange-500",
                    title: "Ending Soon",
                    icon: <Timer className="w-4 h-4 text-orange-500"/>
                };
            } else {
                return {color: "text-green-500", title: "Active", icon: <Timer className="w-4 h-4 text-green-500"/>};
            }
        } else if (status == PollStatus.pending) {
            return {color: "text-red-500", title: "Finished", icon: <TimerReset className="w-4 h-4 text-red-500"/>};
        } else {
            return {color: "text-red-500", title: "Completed", icon: <TimerOff className="w-4 h-4 text-red-500"/>};
        }
    };

    // Check if the poll has exactly two options and they are "Yes" and "No"
    const isYesNoPool = React.useMemo(() => {
        if (poll.options && poll.options.length === 2) {
            const optionTitles = poll.options.map(opt => opt.title.toLowerCase().trim());
            return optionTitles.includes('yes') && optionTitles.includes('no');
        }
        return false;
    }, [poll.options]);

    const status = getPollStatus(poll.endDate, poll.status);
    const isPollExpired = new Date(poll.endDate) < new Date();
    const isHighEngagement = poll.totalVotes >= 10000;

    return (
        <>
            <div
                className="relative overflow-hidden rounded-xl h-[320px] shadow-lg transition-all duration-300 hover:shadow-xl cursor-pointer"
                onClick={handlePollClick}
            >
                {/* Full size background image with overlay */}
                <div className="absolute inset-0 w-full h-full">
                    <img
                        src={poll.imageUrl}
                        alt={poll.title}
                        className="w-full h-full object-cover opacity-80"
                    />
                    {/* Gradient overlay from bottom to title area */}
                    <div
                        className="absolute inset-0 bg-gradient-to-t from-[#454E3D] via-[#454E3D]/25 to-transparent"></div>
                </div>

                {/* Content overlay */}
                <div className="absolute inset-0 flex flex-col justify-between p-4">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                            {status.icon}
                            <span className="text-xs font-medium text-white">{status.title}</span>

                            {isHighEngagement && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="flex items-center">
                                                <Flame className="w-4 h-4 text-orange-500 animate-pulse"/>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>This poll has high engagement!</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>

                        {(!isPollExpired && poll.status == PollStatus.active) && (
                            <CountdownTimer endDate={poll.endDate.toString()}/>
                        )}

                    </div>

                    <div className="space-y-4">
                        {/* Poll title */}
                        <h3 className="font-bold text-xl text-white leading-tight">{poll.title}</h3>

                        {/* Stats row */}
                        <div className="grid grid-cols-3 gap-2 justify-between">
                            <div className="flex items-center text-xs text-white ">
                                <Logs className="w-3 h-3 mr-1 shrink-0"/>
                                <span>{poll.categories?.slug.slice(0, 1).toUpperCase() + poll.categories?.slug.slice(1)}</span>
                            </div>
                            <div className="flex items-center text-xs text-white ">
                                <Trophy className="w-3 h-3 mr-1  text-white/80"/>
                                <span>{poll.prize} bp</span>
                            </div>
                            <div className="flex items-center text-xs text-white ">
                                <Timer className="w-3 h-3 mr-1 text-white/80"/>
                                <span className={isMobile ? "text-xs" : ""}>{formatEndDate(poll.endDate)}</span>
                            </div>
                        </div>

                        {/* Vote buttons - Show Yes/No buttons or Vote Now button */}

                        {isYesNoPool ? (
                            (poll.status == PollStatus.active) ? (
                                <div className="flex gap-2 w-full">
                                    {(userVote?.title == 'Yes') ? (
                                        <Button
                                            className="w-full bg-[#E5F4EA] hover:bg-[#D5E8DC] text-[#155231] font-medium"
                                        >
                                            <Check className="w-4 h-4 mr-1"/> Voted
                                        </Button>
                                    ) : (userVote?.title == 'No') ? (
                                        <Button
                                            className="w-full bg-[#FFF7DE] hover:bg-[#FFE9B0] text-[#6F3B01] font-medium"
                                        >
                                            <Check className="w-4 h-4 mr-1"/> Voted
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                className="flex-1 bg-[#E5F4EA] hover:bg-[#D5E8DC] text-[#155231]"
                                                onClick={(e) => handleVoteClick('yes', 'Yes', e, poll.options.find(o => o.title == 'Yes').id)}
                                            >
                                                <Heart className="w-4 h-4 mr-1"/> Yes
                                            </Button>
                                            <Button
                                                className="flex-1 bg-[#FFF7DE] hover:bg-[#FFE9B0] text-[#6F3B01]"
                                                onClick={(e) => handleVoteClick('no', 'No', e, poll.options.find(o => o.title == 'No').id)}
                                            >
                                                <X className="w-4 h-4 mr-1"/> No
                                            </Button>
                                        </>
                                    )}
                                </div>
                            ) : (<Button
                                className="w-full bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border border-white/10"
                            >
                                See Results
                            </Button>)) : (poll.status == PollStatus.active) ? (
                                <Button
                                    className="w-full bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border border-white/10"
                                >
                                    Vote Now
                                </Button>
                            ) :
                            (<Button
                                className="w-full bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border border-white/10"
                            >
                                See Results
                            </Button>)
                        }
                    </div>
                </div>
            </div>

            <VoteConfirmationModal
                open={showConfirmModal}
                onConfirm={handleConfirmVote}
                onCancel={handleCancelVote}
                pollTitle={poll.title}
                selectedOption={pendingVote?.option || ''}
                optionType={pendingVote?.type || 'yes'}
            />
        </>
    );
};
